import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { UserContext } from "./userContext";
import { loadConnectAndInitialize } from "@stripe/connect-js";
import {
  lookupStripeAccount,
  getStripeAccountSession,
  createStripeAccount,
  updateStripeAccount,
} from "../services/stripe/StripeService";
import { STRIPE_CONFIG } from "../services/stripe/StripeConfig";
import { stripBrandPrefix } from "../../utils/EmpUtil";
import { registerStripeLogout } from "../../common/InvalidateSession";
import { useDealerGroupCount } from "../hooks/useDealerGroupCount";
import { useDealerLocationData } from "../hooks/useDealerLocationData";

const StripeContext = createContext(null);

const STRIPE_STATUS = {
  NOT_STARTED: "not-started",
  PENDING_ACTIONS: "pending-actions",
  ACCOUNT_READY: "account-ready",
};

const isEmptyArray = (arr) => Array.isArray(arr) && arr.length === 0;

const stripSuffixIgnoreCase = (value, suffix) => {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();
  const lowerValue = trimmedValue.toLowerCase();
  const lowerSuffix = suffix.toLowerCase();

  if (!lowerValue.endsWith(lowerSuffix)) {
    return trimmedValue;
  }

  return trimmedValue.slice(0, -suffix.length).trim();
};

const stripPrefixIgnoreCase = (value, prefix) => {
  if (!value) {
    return undefined;
  }

  const trimmedValue = value.trim();
  const lowerValue = trimmedValue.toLowerCase();
  const lowerPrefix = prefix.toLowerCase();

  if (!lowerValue.startsWith(lowerPrefix)) {
    return trimmedValue;
  }

  return trimmedValue.slice(prefix.length).trim();
};

const checkAccountReadiness = (requirements = {}) => {
  const hasPendingVerification = !isEmptyArray(
    requirements.pendingVerification
  );
  const hasCurrentlyDue = !isEmptyArray(requirements.currentlyDue);
  const hasDisabledReasons = requirements.disabledReason !== null;

  let message = "";

  if (hasPendingVerification) {
    message =
      "Your account has items pending verification by Stripe. Please check your Stripe dashboard for details.";
  } else if (hasCurrentlyDue) {
    message =
      "There are additional requirements needed. Please review the info below.";
  } else if (hasDisabledReasons) {
    message = `Your account is currently disabled: ${requirements.disabledReason}`;
  }

  const isReady =
    !hasPendingVerification && !hasCurrentlyDue && !hasDisabledReasons;

  const tabStatus = isReady
    ? STRIPE_STATUS.ACCOUNT_READY
    : STRIPE_STATUS.PENDING_ACTIONS;

  return {
    isReady,
    message,
    eventuallyDueRequirements: requirements.eventuallyDue || [],
    tabStatus,
  };
};

const initializeStripeConnect = async (accountId) => {
  const fetchSecret = async () => getStripeAccountSession(accountId);

  return loadConnectAndInitialize({
    publishableKey: STRIPE_CONFIG.publishableKey,
    fetchClientSecret: fetchSecret,
    appearance: STRIPE_CONFIG.appearance,
  });
};

const parseLocationDataForStripe = (locationData) => {
  if (!locationData) {
    return {};
  }

  const { marketArea, regionDesc, zoneDesc } = locationData;

  let cleanedMarketArea = stripSuffixIgnoreCase(marketArea, " market area");

  if (cleanedMarketArea === marketArea?.trim()) {
    cleanedMarketArea = stripSuffixIgnoreCase(marketArea, "market area");
  }

  let cleanedZoneDesc = stripPrefixIgnoreCase(zoneDesc, "zone ");

  if (cleanedZoneDesc === zoneDesc?.trim()) {
    cleanedZoneDesc = stripPrefixIgnoreCase(zoneDesc, "zone");
  }

  return {
    marketName: cleanedMarketArea,
    regionName: regionDesc ? regionDesc.trim() : undefined,
    zoneCode: cleanedZoneDesc,
  };
};

const getStripeStatusDisplayByStatus = (stripeStatus) => {
  if (stripeStatus === STRIPE_STATUS.ACCOUNT_READY) {
    return {
      chipLabel: "Account Ready",
      chipLabelColor: "success",
    };
  }

  return {
    chipLabel: "Action Required",
    chipLabelColor: "warning",
  };
};

export const useStripeContext = () => {
  const context = useContext(StripeContext);

  if (!context) {
    throw new Error("useStripeContext must be used within a StripeProvider");
  }

  return context;
};

export const StripeProvider = ({ children }) => {
  const [errorMessage, setErrorMessage] = useState("");
  const [statusMessage, setStatusMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [stripeConnectInstance, setStripeConnectInstance] = useState(null);
  const [detailsSubmitted, setDetailsSubmitted] = useState(false);
  const [isAccountEnabled, setIsAccountEnabled] = useState(false);
  const [accountId, setAccountId] = useState("");
  const [createdTimestamp, setCreatedTimestamp] = useState(null);
  const [eventuallyDueRequirements, setEventuallyDueRequirements] = useState(
    []
  );
  const [stripeStatus, setStripeStatus] = useState(STRIPE_STATUS.NOT_STARTED);

  const { user } = useContext(UserContext);

  const salesCodeStripped = stripBrandPrefix(user?.salesCode) || "";
  const { market, dealerId, salesCode, brand } = user || {};
  const dealerCommonId =
    user?.dealerCommonId ||
    user?.commonId ||
    user?.commonDealerId ||
    (user ? `${market}|${dealerId}|${salesCode}|${brand}` : null);

  const { dealerGroupType, isLoading: dealerGroupLoading } =
    useDealerGroupCount(user, salesCodeStripped, user?.dealerId);

  const {
    data: locationData,
    isLoading: locationLoading,
    error: locationError,
  } = useDealerLocationData();

  const isPageLoading = isLoading || locationLoading || dealerGroupLoading;

  useEffect(() => {
    const stripeLogoutFn = () => {
      const hasViewedOnboarding =
        sessionStorage.getItem("stripe.onboardingViewed") === "true";

      if (stripeConnectInstance && hasViewedOnboarding) {
        console.log("Logging out from Stripe Connect instance");
        stripeConnectInstance.logout();
        setStripeConnectInstance(null);
        sessionStorage.removeItem("stripe.onboardingViewed");
      }
    };

    registerStripeLogout(stripeLogoutFn);

    return () => {
      registerStripeLogout(null);
    };
  }, [stripeConnectInstance]);

  const handleInitializeStripe = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      setStatusMessage("");

      try {
        const accountInfo = await lookupStripeAccount(dealerCommonId);

        const {
          isReady,
          message,
          eventuallyDueRequirements: dueReqs,
          tabStatus,
        } = checkAccountReadiness(accountInfo.requirements);

        setIsAccountEnabled(isReady);
        setStatusMessage(message);
        setDetailsSubmitted(accountInfo.detailsSubmitted || false);
        setAccountId(accountInfo.id);
        setEventuallyDueRequirements(dueReqs);
        setStripeStatus(tabStatus);

        if (accountInfo.created) {
          const newDate = new Date(
            accountInfo.created * 1000
          ).toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "numeric",
          });

          setCreatedTimestamp(newDate);
        }

        if (accountInfo.id) {
          const connectInstance = await initializeStripeConnect(accountInfo.id);
          setStripeConnectInstance(connectInstance);
        }
      } catch (error) {
        if (error.message === "ACCOUNT_NOT_FOUND") {
          setIsAccountEnabled(false);
          setErrorMessage("ACCOUNT_NOT_FOUND");
          setStripeStatus(STRIPE_STATUS.NOT_STARTED);
          return;
        }

        throw error;
      }
    } catch (error) {
      setIsAccountEnabled(false);
      setErrorMessage(error.message);
      setStripeStatus(STRIPE_STATUS.NOT_STARTED);
    } finally {
      setIsLoading(false);
    }
  }, [dealerCommonId]);

  const handleCreateAccount = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");

      if (!dealerGroupType) {
        const errorMsg = dealerGroupLoading
          ? "Unable to create Stripe account: Dealer group information is still loading. Please try again in a moment."
          : "Unable to create Stripe account: Required dealer group metadata is not available. Please contact support.";

        setErrorMessage(errorMsg);
        setIsAccountEnabled(false);
        setStripeStatus(STRIPE_STATUS.NOT_STARTED);

        console.error(
          "Stripe account creation failed: Missing dealerGroupType",
          {
            dealerGroupType,
            dealerGroupLoading,
            user: user?.dealerId,
            dealerCommonId,
          }
        );

        return;
      }

      const locationMetadata = parseLocationDataForStripe(locationData);

      if (locationError) {
        console.warn(
          "Location data fetch failed, proceeding without location metadata:",
          locationError
        );
      }

      const createdAccount = await createStripeAccount({
        dealerCommonId,
        dealerName: user?.dealerName || "",
        division: user?.brand || "",
        paCode: user?.dealerId || "",
        dealerType: dealerGroupType,
        marketName: locationMetadata.marketName || "",
        regionName: locationMetadata.regionName || "",
        zoneCode: locationMetadata.zoneCode || "",
      });

      if (createdAccount?.id) {
        setIsAccountEnabled(true);
        setAccountId(createdAccount.id);
        await handleInitializeStripe();
      } else {
        setIsAccountEnabled(false);
        setErrorMessage("Failed to create account");
        setStripeStatus(STRIPE_STATUS.NOT_STARTED);
      }
    } catch (error) {
      setIsAccountEnabled(false);
      setErrorMessage(error.message);
      setStripeStatus(STRIPE_STATUS.NOT_STARTED);
    } finally {
      setIsLoading(false);
    }
  }, [
    dealerGroupType,
    dealerGroupLoading,
    user,
    dealerCommonId,
    locationData,
    locationError,
    handleInitializeStripe,
  ]);

  const handleLogout = useCallback(() => {
    console.log("Stripe session logout called");

    if (stripeConnectInstance) {
      stripeConnectInstance.logout();
      setStripeConnectInstance(null);
      sessionStorage.removeItem("stripe.onboardingViewed");
    }
  }, [stripeConnectInstance]);

  const updateAccount = useCallback(async (accountIdToUpdate, metadata) => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      await updateStripeAccount(accountIdToUpdate, metadata);
    } catch (error) {
      setErrorMessage(error.message);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const getStripeStatusDisplay = useCallback(
    () => getStripeStatusDisplayByStatus(stripeStatus),
    [stripeStatus]
  );

  const contextValue = useMemo(
    () => ({
      errorMessage,
      statusMessage,
      isLoading: isPageLoading,
      stripeConnectInstance,
      detailsSubmitted,
      isAccountEnabled,
      accountId,
      createdTimestamp,
      eventuallyDueRequirements,
      stripeStatus,
      locationLoading,
      dealerGroupLoading,
      handleCreateAccount,
      handleInitializeStripe,
      handleLogout,
      updateAccount,
      getStripeStatusDisplay,
    }),
    [
      errorMessage,
      statusMessage,
      isPageLoading,
      stripeConnectInstance,
      detailsSubmitted,
      isAccountEnabled,
      accountId,
      createdTimestamp,
      eventuallyDueRequirements,
      stripeStatus,
      locationLoading,
      dealerGroupLoading,
      handleCreateAccount,
      handleInitializeStripe,
      handleLogout,
      updateAccount,
      getStripeStatusDisplay,
    ]
  );

  return (
    <StripeContext.Provider value={contextValue}>
      {children}
    </StripeContext.Provider>
  );
};

StripeProvider.propTypes = {
  children: PropTypes.node,
};

StripeProvider.defaultProps = {
  children: null,
};

export { StripeContext };
